import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { isNil } from 'lodash';
import css from './NetworkTable.css';
import { Table as SingularTable } from '../../../../components/widgets';
import { NA_VALUE, REVENUE_KEY_ROWS, ROWS_DATA_KEYS, SESSION } from '../consts';
import { getMetricLabels, getMetricTooltips } from '../reportingUtilsHelper';
import Icon, { IconTypes } from '../../../../components/foundations/Icon';
import TableCell from '../../../../components/widgets/TableCell';
import TableCellText from '../../../../components/widgets/TableCellText';
import { GROUPS_LABELS } from '../../Wizard/consts';

export const TABLE_DATA_TEST_ID = 'ab-testing-table';
const TRANSLATION_PREFIX = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING';

const getTableMetadata = testChannel => ({
    name: testChannel.id.toString(),
    cellValue: [testChannel.adnetworkName],
    cellComponent: TableCellText,
    headerAlign: 'center',
    cellProps: { className: css.cellContent },
});

function NetworkTable({
    testChannels = [],
    tableData = {},
    translate,
    isEmpty = false,
    eventName,
    isDuplicateNetworkSelected = false,
}) {
    const tableMetadata = useMemo(
        () =>
            isEmpty
                ? []
                : [
                      {
                          name: 'metric',
                          displayName: translate(`${TRANSLATION_PREFIX}.REPORTING.TABLE_DATA.METRIC`),
                          cellComponent: TableCell,
                          headerAlign: 'center',
                          cellProps: { className: css.metricCellContent, tooltipText: 'metricTooltip' },
                      },
                      ...testChannels.map((testChannel, index) => ({
                          ...getTableMetadata(testChannel),
                          HeaderIcon: () =>
                              testChannel.isControlGroup ? (
                                  <Icon type={IconTypes.MINGCUTE} name="flask-line" className={css.headerIcon} />
                              ) : (
                                  <img className={css.headerIcon} src={testChannel.adnetworkLogoPath} alt="" />
                              ),
                          displayName: testChannel.isControlGroup
                              ? translate(`${TRANSLATION_PREFIX}.CONTROL_GROUP_LABEL`)
                              : testChannel.adnetworkDisplayName +
                                (isDuplicateNetworkSelected ? ` ${GROUPS_LABELS[index]}` : ''),
                      })),
                  ],
        [testChannels, translate, isEmpty, isDuplicateNetworkSelected]
    );

    const rowsData = useMemo(() => {
        if (isEmpty) return [];
        const metricLabels = getMetricLabels(translate, eventName);
        const metricTooltips = getMetricTooltips(translate);

        return Object.entries(ROWS_DATA_KEYS)
            .map(([metricKey, dataProperty]) => {
                if (eventName === SESSION && REVENUE_KEY_ROWS.includes(dataProperty)) {
                    return null;
                }
                const row = {
                    metric: metricLabels[metricKey],
                    metricTooltip: metricTooltips[metricKey] || '',
                };

                testChannels.forEach(channel => {
                    row[channel.id] = !isNil(tableData[channel.id][dataProperty])
                        ? tableData[channel.id][dataProperty]
                        : NA_VALUE;
                });
                return row;
            })
            .filter(Boolean);
    }, [translate, tableData, testChannels, isEmpty, eventName]);

    return (
        <div className={css.container}>
            <SingularTable
                data={rowsData}
                dataTestId={TABLE_DATA_TEST_ID}
                metadata={tableMetadata}
                thClass={`${css.tableHeader} ${testChannels.length === 2 ? css.third : css.forth}`}
                trClass={css.tableCell}
                zebraTable={false}
                tableClass={css.tableContainer}
                showInnerBorder
                roundBottomCorners
            />
        </div>
    );
}

NetworkTable.propTypes = {
    testChannels: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            groupSize: PropTypes.string,
        })
    ),
    tableData: PropTypes.shape({
        adnetworkName: PropTypes.string,
        adnetworkDisplayName: PropTypes.string,
        adnetworkLogoPath: PropTypes.string,
        isControlGroup: PropTypes.bool,
        groupSize: PropTypes.string,
        eventCount: PropTypes.string,
        engagementRate: PropTypes.string,
        engagementIncrementality: PropTypes.string,
        uniqueEventCount: PropTypes.string,
        uniqueConversionRate: PropTypes.string,
        uniqueIncrementality: PropTypes.string,
        totalRevenue: PropTypes.string,
    }),
    isEmpty: PropTypes.bool,
    isDuplicateNetworkSelected: PropTypes.bool,
    eventName: PropTypes.string,
    translate: PropTypes.func,
};

export default withLocalize(NetworkTable);
